/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, slicedToArray as _slicedToArray } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from '@carbon/react';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';
import { noopFn } from '../../../Utilities/propHelpers/defaultProps.js';
import ErrorState from '../ErrorState/ErrorState.js';

var _excluded = ["open", "onClose", "title", "label", "subtitle", "text", "caption", "status", "error", "recovery", "mock", "shouldSubmitOnEnter"];
var _ErrorModal = function ErrorModal(_ref) {
  var open = _ref.open,
    _ref$onClose = _ref.onClose,
    onClose = _ref$onClose === void 0 ? noopFn : _ref$onClose,
    title = _ref.title,
    label = _ref.label,
    subtitle = _ref.subtitle,
    text = _ref.text,
    caption = _ref.caption,
    _ref$status = _ref.status,
    status = _ref$status === void 0 ? 500 : _ref$status,
    error = _ref.error,
    recovery = _ref.recovery,
    mock = _ref.mock,
    shouldSubmitOnEnter = _ref.shouldSubmitOnEnter,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var isAuthError = status === 401 || status === 403;
  var heading = title || (isAuthError ? t('Not authorized') : t('Something went wrong'));
  if (mock) {
    return (
      /*#__PURE__*/
      // eslint-disable-next-line react/jsx-pascal-case
      React.createElement(_ErrorModal.mock, _extends({
        title: title,
        label: label,
        subtitle: subtitle,
        text: text,
        caption: caption,
        status: status,
        error: error,
        recovery: recovery
        // eslint-disable-next-line react/jsx-props-no-spreading
      }, rest))
    );
  }
  return /*#__PURE__*/React.createElement(Modal, _extends({
    modalHeading: heading,
    modalLabel: label,
    open: open,
    onRequestClose: onClose,
    danger: isAuthError,
    passiveModal: true,
    className: "cpc--error-modal"
    // elements with role 'presentation' should not have an aria-label
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/presentation_role
    // aria-label={heading}
    ,
    shouldSubmitOnEnter: shouldSubmitOnEnter
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, rest), /*#__PURE__*/React.createElement(ErrorState, {
    status: status,
    subtitle: subtitle,
    text: text,
    caption: caption,
    error: error,
    recovery: recovery
  }));
};
_ErrorModal.mock = function (props) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      return setOpen(true);
    }
  }, "Click me"), /*#__PURE__*/React.createElement(_ErrorModal, _extends({}, props, {
    open: open,
    onClose: function onClose() {
      return setOpen(false);
    }
  })));
};
_ErrorModal.propTypes = {
  /**
   * Determines if the modal is displayed or not.
   */
  open: PropTypes.bool,
  /**
   * Function to call when the modal is closed. Use this to set the `open` prop back to `false`.
   */
  onClose: PropTypes.func,
  /**
   * Header text to display
   */
  title: PropTypes.string,
  /**
   * Label to be displayed above the title
   */
  label: PropTypes.string,
  /**
   * Content to display immediately preceding the `text`. Use this to include an explanation of
   * the action that was taking place that caused the error, and still have the generic error
   * text displayed afterward.
   */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Text to show in the error message (see Message component).
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Caption to show in the error message (see Message component).
   */
  caption: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * The error status code.
   */
  status: PropTypes.number,
  /**
   * An error response object. If provided, the status, text, and caption will be obtained by
   * parsing the error using the `parseError()` function from the `request` util.
   */
  error: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  /**
   * Internal use only.
   */
  mock: PropTypes.bool,
  /**
   * Specify if Enter key should be used as "submit" action
   */
  shouldSubmitOnEnter: PropTypes.bool,
  /**
   * Additional helpful content to show after the main content of the error. Use this along
   * with the `error` prop to add content after the message extracted from the error, for things
   * like links to docs or other helpful recovery actions.
   */
  recovery: PropTypes.node
};
var ErrorModal = _ErrorModal;

export { ErrorModal as default };
